import { useRef, useState, type ReactNode } from 'react';
import { civilDateBounds, isCivilDate } from './civil-date';
import { DATE_SHORTCUTS, addCivilDays, civilToPadText, isWeekend, longCivilDate, padDateCivil, padDateFromTyping, padDateState, padDateStep, padDateValue, quickDateLabel, quickDates, recentDays } from './pad-input';

/** Le champ suivant du formulaire (une date complète y passe, comme le pavé de la tablette). */
function focusNext(from: HTMLElement | null): void {
  const form = from?.closest('form') ?? document;
  const fields = [...form.querySelectorAll<HTMLElement>('input:not([type="hidden"]):not([disabled]), select:not([disabled]), textarea:not([disabled])')];
  const index = from === null ? -1 : fields.indexOf(from);
  fields[index + 1]?.focus();
}

/**
 * Date civile tapée JJ/MM/AA (ADR-141 §3 bis et addendum (2) du 2026-10-19, même saisie que le pavé de la
 * tablette) : six chiffres suffisent, huit (JJ/MM/AAAA, tapés ou collés) restent admis ; le siècle est
 * celui qui place la date dans les bornes du calendrier. Date relue en toutes lettres ; une date complète
 * et valable passe au champ suivant. Raccourcis Aujourd'hui / Hier / Avant-hier et ± 1 jour, ou, quand le
 * gabarit déclare `presentation.quick_dates`, une grille de dates rapides sous le champ (libellés du v1,
 * week-ends teintés) et « Plus de dates » (jours récents jusqu'à la borne basse). « Aujourd'hui » et les
 * bornes (J − 2 ans, J + 1 an) se calculent au fuseau de la tâche (`civil_timezone`) ; sans fuseau, ni
 * raccourcis ni bornes. Le brouillon fait foi : une date impossible, ambiguë ou hors période reste
 * affichée et signalée, et aucune valeur ne part. Un instant reçu n'est jamais converti : il est signalé.
 */
export function PadDateInput({
  id,
  value,
  onChange,
  disabled,
  invalid,
  describedBy,
  required,
  today,
  quick = [],
}: {
  id: string;
  value: unknown;
  onChange: (value: unknown) => void;
  disabled: boolean;
  invalid: boolean;
  describedBy: string | undefined;
  required: boolean;
  /** Date du jour civile au fuseau de la tâche ; `null` sans fuseau connu. */
  today: string | null;
  /** Jetons des dates rapides (`presentation.quick_dates`, inspection-template-body 1.7.0). */
  quick?: readonly string[];
}) {
  const [draft, setDraft] = useState(() => civilToPadText(value));
  const [more, setMore] = useState(false);
  const input = useRef<HTMLInputElement | null>(null);
  const bounds = civilDateBounds(today);
  // La valeur gardée change d'ailleurs (réinitialisation) : le brouillon la suit, sauf s'il la représente déjà.
  const shownDraft = isCivilDate(value) && padDateCivil(draft, bounds) !== value ? civilToPadText(value) : draft;
  const foreign = typeof value === 'string' && value !== '' && !isCivilDate(value);
  const state = padDateState(shownDraft, bounds);
  const feedbackId = `${id}-feedback`;
  const offered = quickDates(quick, today);

  const write = (text: string): void => {
    setDraft(text);
    onChange(padDateValue(text, bounds));
  };
  /** Un jour choisi (date rapide, grille) : la date est remplie, le curseur passe au champ suivant. */
  const choose = (civil: string): void => {
    write(civilToPadText(civil));
    setMore(false);
    focusNext(input.current);
  };

  const feedback =
    state === 'valid'
      ? { tone: 'success', text: `✓ ${longCivilDate(padDateCivil(shownDraft, bounds)!)}` }
      : state === 'impossible'
        ? { tone: 'danger', text: 'Cette date n’existe pas.' }
        : state === 'out_of_range'
          ? { tone: 'danger', text: `Hors période : du ${civilToPadText(bounds!.min)} au ${civilToPadText(bounds!.max)}.` }
          : state === 'ambiguous'
            ? { tone: 'danger', text: 'Siècle ambigu : tapez l’année sur quatre chiffres (JJ/MM/AAAA).' }
            : { tone: 'neutral', text: 'Tapez JJ/MM/AA' };
  const chosen = padDateCivil(shownDraft, bounds);
  const chip = (civil: string, key: string): ReactNode => (
    <button
      key={key}
      type="button"
      className={`ay-paddate__chip${isWeekend(civil) ? ' ay-paddate__chip--weekend' : ''}${chosen === civil ? ' ay-paddate__chip--selected' : ''}`}
      aria-pressed={chosen === civil}
      data-testid={`quick-${civil}`}
      disabled={disabled}
      onClick={() => choose(civil)}
    >
      {quickDateLabel(civil, today!)}
    </button>
  );

  return (
    <div className="ay-paddate">
      <input
        ref={input}
        id={id}
        type="text"
        inputMode="numeric"
        autoComplete="off"
        placeholder="JJ/MM/AA"
        maxLength={10}
        className="ay-paddate__input"
        required={required}
        disabled={disabled}
        aria-invalid={invalid || foreign || state === 'impossible' || state === 'out_of_range' || state === 'ambiguous'}
        aria-describedby={[describedBy, feedbackId].filter(Boolean).join(' ')}
        value={shownDraft}
        onChange={(event) => {
          const next = padDateFromTyping(event.target.value);
          const from = event.currentTarget;
          write(next);
          // Six chiffres (JJ/MM/AA) ou huit (JJ/MM/AAAA) valables : la date est complète, le curseur passe au suivant.
          const digits = next.replace(/\//g, '').length;
          if (next !== shownDraft && (digits === 6 || digits === 8) && padDateState(next, bounds) === 'valid') focusNext(from);
        }}
      />
      <span id={feedbackId} className={`ay-paddate__feedback ay-paddate__feedback--${feedback.tone}`} aria-live="polite">
        {feedback.text}
      </span>
      {foreign && <span className="ay-field__error">Valeur reçue « {String(value)} » : ce n’est pas une date civile (AAAA-MM-JJ) ; tapez la date.</span>}
      {offered.length > 0 ? (
        <>
          <div className="ay-paddate__quick" role="group" aria-label="Dates rapides" data-testid="quick-dates">
            {offered.map((entry) => chip(entry.date, entry.token))}
            <button type="button" className="ay-paddate__chip ay-paddate__chip--more" aria-expanded={more} disabled={disabled} onClick={() => setMore(!more)}>
              Plus de dates
            </button>
          </div>
          {more && (
            <div className="ay-paddate__quick ay-paddate__recent" role="group" aria-label="Jours récents" data-testid="recent-days">
              {recentDays(today!, bounds).map((civil) => chip(civil, civil))}
            </div>
          )}
        </>
      ) : (
        <div className="ay-paddate__shortcuts" role="group" aria-label="Raccourcis de date">
          {today !== null &&
            DATE_SHORTCUTS.map((shortcut) => {
              const date = addCivilDays(today, shortcut.days);
              return (
                <button key={shortcut.days} type="button" className={`ay-button ay-button--secondary ay-paddate__shortcut${chosen === date ? ' ay-paddate__shortcut--selected' : ''}`} aria-pressed={chosen === date} disabled={disabled} onClick={() => write(civilToPadText(date))}>
                  {shortcut.label}
                </button>
              );
            })}
          <button type="button" className="ay-button ay-button--secondary" disabled={disabled || (chosen === null && today === null)} onClick={() => write(padDateStep(shownDraft, -1, today))}>
            − 1 jour
          </button>
          <button type="button" className="ay-button ay-button--secondary" disabled={disabled || (chosen === null && today === null)} onClick={() => write(padDateStep(shownDraft, 1, today))}>
            + 1 jour
          </button>
        </div>
      )}
    </div>
  );
}
